import { useTranslation } from 'react-i18next'
import { styled } from 'styled-components'

import { Body } from '@/design-system'
import {
	GroupeDeQuestionsPublicodes,
	QuestionPublicodes,
} from '@/domaine/questions'
import { Situation } from '@/domaine/Situation'

import { useAutoScrollToQuestions } from './AutoScrollToQuestions'
import { BlocMontants } from './BlocMontants'
import { BlocSituation } from './BlocSituation'
import {
	ComposantQuestionFournie,
	GroupeDeQuestionsFournies,
} from './Questions/ComposantQuestionFournie'

type Props<S extends Situation> = {
	montants: React.ReactNode
	questionsPublicodesPrincipales?: QuestionPublicodes[]
	groupesDeQuestionsPublicodes?: Record<string, GroupeDeQuestionsPublicodes>
	questionsFourniesPrincipales?: ComposantQuestionFournie<S>[]
	groupesDeQuestionsFournies?: Record<string, GroupeDeQuestionsFournies<S>>
	situation?: S
	questionsPrincipalesRépondues?: boolean
	onReset: () => void
}

export const ZoneDeSaisie = <S extends Situation = Situation>({
	montants,
	questionsPublicodesPrincipales,
	groupesDeQuestionsPublicodes,
	questionsFourniesPrincipales,
	groupesDeQuestionsFournies,
	situation,
	questionsPrincipalesRépondues,
	onReset,
}: Props<S>) => {
	const { t } = useTranslation()
	const { setAutoScrollToQuestions } = useAutoScrollToQuestions()

	return (
		<>
			<BodyWithoutMargin>
				{t(
					'components.simulateur.zone-de-saisie.info-mise-à-jour',
					'Les données de simulations se mettront automatiquement à jour après la modification d’un champ.'
				)}
			</BodyWithoutMargin>

			<Container>
				<LeftColumn onFocus={() => setAutoScrollToQuestions(true)}>
					<BlocSituation
						questionsPublicodesPrincipales={questionsPublicodesPrincipales}
						groupesDeQuestionsPublicodes={groupesDeQuestionsPublicodes}
						questionsFourniesPrincipales={questionsFourniesPrincipales}
						groupesDeQuestionsFournies={groupesDeQuestionsFournies}
						situation={situation}
						questionsPrincipalesRépondues={questionsPrincipalesRépondues}
						onReset={onReset}
					/>
				</LeftColumn>
				<RightColumn onFocus={() => setAutoScrollToQuestions(false)}>
					<BlocMontants>{montants}</BlocMontants>
				</RightColumn>
			</Container>
		</>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	flex-wrap: wrap;
	gap: ${({ theme }) => theme.spacings.xl};
	@media (min-width: ${({ theme }) => theme.breakpointsWidth.lg}) {
		flex-direction: row;
	}
`

const LeftColumn = styled.div`
	@media (min-width: ${({ theme }) => theme.breakpointsWidth.lg}) {
		width: calc(50% - ${({ theme }) => theme.spacings.md});
	}
`

const RightColumn = styled.div`
	@media (min-width: ${({ theme }) => theme.breakpointsWidth.lg}) {
		width: calc(50% - ${({ theme }) => theme.spacings.md});
	}
`

const BodyWithoutMargin = styled(Body)`
	margin: 0;
`
